import { useCallback, useEffect, useMemo } from 'react';
import { useChannel, useChannelState, useMyConnectionId } from 'services';
import { usePresenceUsers } from 'components/PresenceUsers';
import { getUserTitle } from 'modules/core/helpers';
import type { CellSelectArgs, PresenceMarker } from 'components/DataGrid';
import type { GridSummaryRow, RowOrGroup } from '../types';

type PlanPresence = {
  markers: PresenceMarker[];
  onSelectedCellChange: (args: CellSelectArgs<RowOrGroup, GridSummaryRow> | null) => void;
};

/**
 * Plan-domain presence: turns the block channel's opaque presence blobs (focused
 * `{ rowKey, columnKey }`) into grid markers, and reports our own selection.
 * Clears our presence on unmount so leaving the plan removes the marker for others.
 */
export function usePlanPresence(): PlanPresence {
  const channel = useChannel();
  const { presence } = useChannelState(channel);
  const { color, user } = usePresenceUsers();
  const myConnectionId = useMyConnectionId();

  const onSelectedCellChange = useCallback(
    (args: CellSelectArgs<RowOrGroup, GridSummaryRow> | null) => {
      channel.setPresence(args?.row ? { rowKey: args.row._key, columnKey: args.column.key } : null);
    },
    [channel],
  );

  // Clear our marker for others when leaving the plan.
  useEffect(() => {
    return () => channel.setPresence(null);
  }, [channel]);

  const markers = useMemo<PresenceMarker[]>(
    () =>
      presence
        .filter((v) => v.data && v.connection_id !== myConnectionId)
        .map((v) => {
          const { rowKey, columnKey } = v.data as { rowKey: string; columnKey: string };
          const u = user(v.user_id);
          return {
            rowKey,
            columnKey,
            color: color(v.user_id),
            title: u ? getUserTitle(u) : undefined,
          };
        }),
    [presence, myConnectionId, user, color],
  );

  return { markers, onSelectedCellChange };
}
